import type { PointerEvent as ReactPointerEvent } from 'react'
import { useRef, useState } from 'react'

type Props = {
  label: string
  value: string | undefined
  onChange: (dataUrl: string) => void
}

export function SignaturePad({ label, value, onChange }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const moved = useRef(false)
  const [hasInk, setHasInk] = useState(Boolean(value))

  // The canvas keeps a fixed 600x200 drawing surface for the signed PDF but is displayed at the
  // form's width, so a screen position has to be scaled into canvas pixels.
  const positionOf = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const canvas = event.currentTarget
    const rect = canvas.getBoundingClientRect()
    return {
      x: ((event.clientX - rect.left) * canvas.width) / rect.width,
      y: ((event.clientY - rect.top) * canvas.height) / rect.height,
    }
  }

  const start = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    const context = canvasRef.current?.getContext(`2d`)
    if (!context) return
    drawing.current = true
    moved.current = false
    context.lineWidth = 2
    context.lineCap = `round`
    context.strokeStyle = `#111827`
    context.beginPath()
    const { x, y } = positionOf(event)
    context.moveTo(x, y)
  }

  const move = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    const context = canvasRef.current?.getContext(`2d`)
    if (!context) return
    moved.current = true
    const { x, y } = positionOf(event)
    context.lineTo(x, y)
    context.stroke()
  }

  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    // A stray tap is not a signature, so an unmoved stroke reports nothing.
    if (!moved.current) return
    setHasInk(true)
    onChange(canvasRef.current?.toDataURL(`image/png`) ?? ``)
  }

  const clear = () => {
    const canvas = canvasRef.current
    canvas?.getContext(`2d`)?.clearRect(0, 0, canvas.width, canvas.height)
    setHasInk(false)
    onChange(``)
  }

  return (
    <div className="flex flex-col gap-2">
      <canvas
        ref={canvasRef}
        aria-label={label}
        width={600}
        height={200}
        className="w-full touch-none rounded-lg border border-strong bg-surface"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
      />
      <div className="flex items-center justify-between">
        {hasInk ? (
          <span className="text-sm text-subtle">Signed</span>
        ) : (
          <span className="text-sm text-subtle">Sign with your finger or mouse</span>
        )}
        {hasInk && (
          <button type="button" onClick={clear} className="text-sm text-subtle underline">
            Clear
          </button>
        )}
      </div>
    </div>
  )
}
